import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Icon,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  Box,
} from "@chakra-ui/react";
import { FaPlus, FaSchool, FaChalkboardTeacher, FaUser } from "react-icons/fa"; // Import FaPlus from react-icons
import Table_librarian from "./Table_librarian";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const CreateLibrarian = () => {
  const { isOpen, onOpen, onClose } = useDisclosure(); // Chakra UI modal hooks
  const teacherId = sessionStorage.getItem("staff_id");
  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");

  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(""); // Store selected teacher
  const [errorMessage, setErrorMessage] = useState("");
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  // Function to fetch teachers
  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const jwt = sessionStorage.getItem("jwt");
      const school_id = sessionStorage.getItem("school_id"); // If JWT is needed

      const response = await axios.get(
        `${BASE_URL}/admin/staff/staff_list.php?school_id=${school_id}`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      // setTeachers(response.data.data);
      setTeachers(Array.isArray(response.data.data) ? response.data.data : []);

      // if (response.data.status === "success") {
      //   setTeachers(response.data.data);
      // } else {
      //   setErrorMessage(response.data.message || "Error fetching teachers");
      // }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setErrorMessage("Network Error: " + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // Fetch teachers when component mounts
  useEffect(() => {
    fetchTeachers();
  }, []);

  // Handle librarian creation
  const handleCreateLibrarian = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/library/librarian/add_librarian.php`, // PHP endpoint
        {
          user_id: userId,
          school_id: schoolId,
          teacher_id: selectedTeacherId, // Use selected teacher ID here
        }
      );

      // Check if the API response is successful
      if (response.data.success) {
        toast({
          title: "Librarian Created.",
          description: "The librarian was created successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose(); // Close modal after success
      } else {
        setErrorMessage(response.data.message); // Show error message from API
      }
    } catch (error) {
      setErrorMessage("An error occurred while creating the librarian.");
    }
  };

  // Handle the view button click (you can modify this to show a different view)
  const handleViewClick = () => {
    toast({
      title: "View Button Clicked.",
      description:
        "Here you can display the view details or navigate somewhere.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <div>
      <Button
        colorScheme="yellow"
        onClick={onOpen}
        size={{ base: "xs", md: "md" }}
        leftIcon={<FaPlus />} // Replaced with FaPlus from react-icons
      >
        Librarian
      </Button>

      {/* Modal for librarian creation */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Create Librarian
            {/* View button positioned on the left */}
            <Box display="flex" justifyContent="end" w="100%">
              <Box mr={7}>
                <Table_librarian />
              </Box>
            </Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {/* Display error message if any */}
            {errorMessage && (
              <div style={{ color: "red", marginBottom: "10px" }}>
                {errorMessage}
              </div>
            )}
            {/* User ID input field */}
            <FormControl id="user-id" mb={4}>
              <FormLabel>
                {" "}
                <Icon as={FaUser} mr={1} color="teal.500" />
                User ID
              </FormLabel>
              <Input
                type="text"
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                value={userId} // Assuming user_id is stored in session storage
                readOnly // Read-only because the user ID is coming from session storage
                required
              />
            </FormControl>
            {/* Teacher ID dropdown */}
            <FormControl id="teacher-id" mb={4}>
              <FormLabel>
                {" "}
                <Icon as={FaChalkboardTeacher} mr={1} color="teal.500" />
                Teacher
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Teacher"
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)} // Update selected teacher ID
                required
              >
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.name}{" "}
                    {/* Assuming teacher object has id and name */}
                  </option>
                ))}
              </Select>
            </FormControl>
            {/* School ID input field */}
            <FormControl id="school-id" mb={4}>
              <FormLabel>
                {" "}
                <Icon as={FaSchool} mr={1} color="teal.500" />
                School ID
              </FormLabel>
              <Input
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                type="number"
                value={schoolId}
                readOnly // Assuming the school ID comes from session storage
                required
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            {/* Button to create librarian */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleCreateLibrarian}
            >
              Create
            </Button>
            {/* Button to close modal */}
            {/* <Button variant="ghost" onClick={onClose} ml={3}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CreateLibrarian;
